// Web Push (PARITY P094/P250 web, 2026-10-24): the browser's PushSubscription packed into the Mattermost session's
// device id, so the push relay on the chat box needs no registry. Mattermost 11 accepts only `<platform>_rn-vN:`
// prefixes; `android_rn-v4` is ours (no official client uses it) and the relay sends Web Push for it.
// Token = base64url( 1 | endpoint-prefix code | auth (16) | p256dh (65) | rest of the endpoint ). The relay's twin
// is /root/satori-push/webpush.py (decode_token) — same codes, same order.

export const WEB_PUSH_PLATFORM = 'android_rn-v4';
/** The relay's VAPID public key (P-256, uncompressed, base64url). Public by design. */
export const VAPID_PUBLIC_KEY = 'BG0kMGPtkBH0dav2fXDPBmo9s-f_7qU9crPhQxj1rXU-iX1pI3t2I0FLusUqYAkW1myTgkzbW4D9YB2zID-oDCw';
/** Mattermost's `sessions.deviceid` is varchar(512). */
export const MAX_DEVICE_ID = 512;

const PREFIXES: Array<[number, string]> = [
  [1, 'https://fcm.googleapis.com/fcm/send/'],
  [2, 'https://updates.push.services.mozilla.com/wpush/v2/'],
  [3, 'https://web.push.apple.com/'],
  [4, 'https://wns2-'],
];

export function base64UrlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export const WebPushToken = {
  /** The device id for this subscription, or null when it does not fit (or is not a usable subscription). */
  deviceId(endpoint: string, p256dh: Uint8Array, auth: Uint8Array): string | null {
    if (!endpoint.startsWith('https://') || p256dh.length !== 65 || auth.length !== 16) return null;
    let code = 0;
    let rest = endpoint;
    for (const [c, prefix] of PREFIXES) {
      if (endpoint.startsWith(prefix)) {
        code = c;
        rest = endpoint.slice(prefix.length);
        break;
      }
    }
    const tail = new TextEncoder().encode(rest);
    const raw = new Uint8Array(2 + 16 + 65 + tail.length);
    raw[0] = 1;
    raw[1] = code;
    raw.set(auth, 2);
    raw.set(p256dh, 18);
    raw.set(tail, 83);
    const id = `${WEB_PUSH_PLATFORM}:${bytesToBase64Url(raw)}`;
    return id.length <= MAX_DEVICE_ID ? id : null;
  },

  /** The inverse (tests and diagnostics): what the relay will read. */
  decode(deviceId: string): { endpoint: string; p256dh: Uint8Array; auth: Uint8Array } | null {
    if (!deviceId.startsWith(WEB_PUSH_PLATFORM + ':')) return null;
    const raw = base64UrlToBytes(deviceId.slice(WEB_PUSH_PLATFORM.length + 1));
    if (raw.length < 84 || raw[0] !== 1) return null;
    const prefix = PREFIXES.find(([c]) => c === raw[1])?.[1] ?? '';
    return { endpoint: prefix + new TextDecoder().decode(raw.slice(83)), auth: raw.slice(2, 18), p256dh: raw.slice(18, 83) };
  },
};
